/* eslint-disable max-params */
/* eslint-disable @typescript-eslint/naming-convention */
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';

import {
  ButtonDirective,
  GridViews,
  WcgButtonComponent,
} from '@wcg/frontend-framework';

import {
  EditGridView,
  EditGridViewForm,
  GridType,
  GridView,
  GridViewDialogData,
  GridViewForm,
} from '@data-access/grid-views';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  minTrimmedLengthValidator,
  noWhitespaceValidator,
} from '@data-access/shared';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    ButtonDirective,
    WcgButtonComponent,
    ReactiveFormsModule,
    NgTemplateOutlet,
    MatInputModule,
    MatDividerModule,
  ],
  selector: 'wcg-grid-views-dialog',
  styleUrls: ['./grid-views.dialog.component.scss'],
  templateUrl: './grid-views.dialog.component.html',
})
export class GridViewsDialogComponent {
  private readonly _dialogRef = inject(MatDialogRef<GridViewsDialogComponent>);
  private readonly _fb = inject(FormBuilder);
  private readonly _destroyRef = inject(DestroyRef);
  public readonly dialogProps =
    inject<Omit<GridViewDialogData<GridViews>, 'config'>>(MAT_DIALOG_DATA);

  viewDialogForm: FormGroup<GridViewForm> = this._createForm();

  gridNames = signal<FormArray<EditGridViewForm>>(
    this.viewDialogForm.get('gridNames') as FormArray<EditGridViewForm>,
  );

  constructor() {
    if (this.dialogProps.viewAction === 'Edit') {
      this._updateViewDialogForm(
        this.dialogProps.bulkUpdateGrids
          ?.slice(0, 5) //This will be removed once the restriction applies
          .map((g) => ({ name: g.name, id: g.id })) as EditGridView[],
      );
    }
    this._customListeners();
  }

  close() {
    this._dialogRef.close();
  }

  saveEditGridView(): void {
    switch (this.dialogProps.viewAction.toLowerCase()) {
      case 'save':
      case 'create':
        this.dialogProps.gridViewAbstract?.saveGridView(
          this._constructData(
            this.viewDialogForm.get('gridNames')?.value as string,
          ),
        );
        break;
      case 'update':
        this.dialogProps.gridViewAbstract?.updateGridView(
          this._constructData(),
        );
        break;
      case 'edit':
        this.dialogProps.gridViewAbstract?.bulkUpdateGridViews({
          grid_type: this.dialogProps.gridProps?.grid_type as GridType,
          grid_views: this._constructEdit(),
        });
        break;
      default:
        this._dialogRef.close();
        break;
    }
  }

  deleteGridView(index: number): void {
    this.gridNames.update((arr) => {
      arr.removeAt(index);
      return arr;
    });
  }

  private _createForm(): FormGroup {
    const gridNames = () => {
      return this.dialogProps.viewAction.toLowerCase() === 'save' ||
        this.dialogProps.viewAction.toLowerCase() === 'create'
        ? (new FormControl('', [
            Validators.required,
            minTrimmedLengthValidator(3),
          ]) as FormControl<string>)
        : (this._fb.array<EditGridViewForm>([]) as FormArray<EditGridViewForm>);
    };

    return this._fb.group<GridViewForm>({
      gridNames: gridNames(),
    });
  }

  private _constructData(gridName?: string): GridView {
    return {
      advanced_filters: this.dialogProps.data?.advancedFilters,
      columns: this.dialogProps.data?.columns,
      description: this.dialogProps.gridProps?.description ?? '',
      grid_type: this.dialogProps.gridProps?.grid_type as GridType,
      id: this.dialogProps.gridProps?.id ?? '',
      name: gridName?.trim() ?? this.dialogProps.gridProps?.name ?? '',
      pagination: this.dialogProps.data?.pagination ?? 10,
      search_string: this.dialogProps.data?.searchString ?? '',
      sorted: this.dialogProps.data?.sorted ?? undefined,
    };
  }

  private _customListeners(): void {
    if (this.dialogProps.$canCloseDialog !== undefined) {
      toObservable(this.dialogProps.$canCloseDialog!)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (canClose) => {
            if (canClose) {
              this._dialogRef.close();
            }
          },
        });
    }
  }

  private _updateViewDialogForm(gNames: EditGridView[]): void {
    if (this.dialogProps.viewAction === 'Edit') {
      /**
       * getFormGroup will generate a FormGroup
       * with FormControls name and id.
       * [k in keyof EditGridView] extract keys of EditGridView.
       * */
      const getFormGroup = (name: string, id: string): EditGridViewForm => {
        return this._fb.group<{
          [k in keyof EditGridView]: FormControl<string>;
        }>({
          id: new FormControl(id, { nonNullable: true }),
          name: new FormControl(name, {
            nonNullable: true,
            validators: [
              noWhitespaceValidator,
              Validators.required,
              Validators.minLength(3),
            ],
          }),
        });
      };

      this.gridNames.update((arr) => {
        for (const g of gNames) {
          arr.push(getFormGroup(g.name as string, g.id as string));
        }
        return arr;
      });
    }
  }

  private _constructEdit(): GridView[] {
    let updatedGrids: GridView[] = [];
    if (this.gridNames().value.length > 0) {
      updatedGrids = this.dialogProps.bulkUpdateGrids?.reduce((acc, d) => {
        for (const g of this.gridNames().value) {
          if (d.id == g.id) {
            acc.push({
              advanced_filters: d.advancedFilters,
              columns: d.columns,
              grid_type: d.gridType as GridType,
              id: d.id,
              name: g.name as string,
              sorted: d.sorted,
            });
          }
        }
        return acc;
      }, [] as GridView[]) as GridView[];
    }
    return updatedGrids;
  }
}
